import * as z from 'zod';

import { catalystSubcategoriesSchema } from '../catalyst-insights';
import { insightIconDisplayRuleZodEnum } from '../explore-filter-schema';
import { marketHoursEnum } from '../market-overview';
import { customFilter } from '../screener';

const eventFeedBasePath = 'event-feed/v1/';

const eventFeedTypeZodEnum = z.enum([
  'scenario-insight',
  'earnings-insight',
  'attribution-insight',
  'market-overview',
]);
export type EventFeedType = z.infer<typeof eventFeedTypeZodEnum>;

export const EVENT_FEED_SCENARIO_FILTERS = 'scenario';
export const EVENT_FEED_CATALYST_COMPANY_FILTERS = 'company_catalyst';
export const EVENT_FEED_CATALYST_MARKET_FILTERS = 'market_catalyst';
export const EVENT_FEED_EARNINGS_FILTERS = 'earnings';

export const eventFeedFilterGroupEnum = z.enum([
  EVENT_FEED_SCENARIO_FILTERS,
  EVENT_FEED_CATALYST_COMPANY_FILTERS,
  EVENT_FEED_CATALYST_MARKET_FILTERS,
  EVENT_FEED_EARNINGS_FILTERS,
]);
export type EventFeedFilterGroupKeys = z.infer<typeof eventFeedFilterGroupEnum>;

const eventFeedFilterOptionSchema = z.object({
  key: z.string(),
  name: z.string(),
  value: z.record(z.string(), z.unknown()).optional(),
});

const eventFeedFilterSchema = z.object({
  key: z.string(),
  name: z.string().optional(),
  config: z.object({
    multiselect: z.boolean(),
    options: z.array(eventFeedFilterOptionSchema),
    custom: customFilter.optional(),
  }),
  visible: z.boolean().optional(),
  locked: z.boolean().optional(),
});

export const eventFeedFiltersSchemas = z.object({
  groups: z.array(
    z.object({
      group: eventFeedFilterGroupEnum,
      group_name: z.string().optional(),
      schemas: z.array(eventFeedFilterSchema),
    })
  ),
});

export type EventFeedFiltersSchemas = z.infer<typeof eventFeedFiltersSchemas>;
export type EventFeedFilter = z.infer<typeof eventFeedFilterSchema>;
export type EventFeedFilters = EventFeedFilter[];

const eventFeedRequestFiltersSchema = z.record(
  z.string(),
  z.object({
    values: z.array(z.record(z.string(), z.unknown())),
  })
);
export type EventFeedRequestFilters = z.infer<
  typeof eventFeedRequestFiltersSchema
>;

/**
 * @deprecated
 */
export const eventFeedRequestSchema = z.object({
  watchlists: z.array(z.string()).optional(),
  competitors: z.boolean().optional(),
  entities: z.array(z.string()).optional(),
  type: eventFeedTypeZodEnum.optional(),
  filters: eventFeedRequestFiltersSchema.optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  limit: z.number().int().optional(),
  next: z.string().optional(),
  prev: z.string().optional(),
  asc: z.boolean().optional(),
});

export const eventFeedRequestSchemaV2 = z.object({
  watchlists: z.array(z.string()).optional(),
  competitors: z.boolean().optional(),
  entities: z.array(z.string()).optional(),
  themes: z.array(z.string()).optional(),
  geographies: z.array(z.string()).optional(),
  type: eventFeedTypeZodEnum.optional(),
  filters: eventFeedRequestFiltersSchema.optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  limit: z.number().int().optional(),
  next: z.string().optional(),
  prev: z.string().optional(),
  asc: z.boolean().optional(),
});

export const eventFeedRequestSchemaV3 = eventFeedRequestSchemaV2.extend({
  categories: z.array(z.string()).optional(),
  sub_categories: z.array(z.string()).optional(),
});

export const marketCatalystSubCategoryEnum = z.enum([
  'thematic',
  'macroeconomic_policy_changes',
  'geopolitical_global_events',
]);
export type MarketCatalystSubCategory = z.infer<
  typeof marketCatalystSubCategoryEnum
>;

const relevanceConnectionSchema = z.object({
  entity: z.string(),
  watchlist_ids: z.array(z.string()).nullish(),
});

const relevanceTierSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type RelevanceTier = z.infer<typeof relevanceTierSchema>;

const relevanceSchema = z.object({
  tier: relevanceTierSchema,
  connections: z.array(relevanceConnectionSchema).nullish(),
  watchlist_ids: z.array(z.string()).nullish(),
});

const eventMetadataSchema = z.object({
  earnings_id: z.string().nullish(),
  earnings_type: z.enum(['preview', 'recap']).nullish(),
  median_return: z.number().nullish(),
  horizon: z.string().nullish(),
  stars: z.number().nullish(),
  episode_count: z.number().nullish(),
  action_type: z.string().nullish(),
  category: z.enum(['market_wide', 'company']).nullish(),
  sub_category: catalystSubcategoriesSchema.nullish().or(z.literal('')),
  identifier: z.string().nullish(),
  snakes: z.array(z.string()).nullish(),
  kind: z.union([insightIconDisplayRuleZodEnum, z.literal('')]),
  market_hours: marketHoursEnum.nullish(),
});

const eventMetadataSchemaV3 = eventMetadataSchema.extend({
  category: z.enum([
    'company_catalyst',
    'market_catalyst',
    'earnings_preview',
    'earnings_recap',
  ]),
  sub_category: catalystSubcategoriesSchema.optional(),
  subject_id: z.string().optional(),
  subject_type: z.enum(['entity', 'theme', 'geo']).optional(),
  subject_ref_id: z.string().optional(),
  subject_ref_type: z.enum(['earnings']).optional(),
});

const eventSchema = z.object({
  id: z.string(),
  source_id: z.string(),
  type: eventFeedTypeZodEnum,
  entity: z.string(),
  created_at: z.string(),
  title: z.string(),
  summary: z.string().nullish(),
  metadata: z
    .object({
      earnings_id: z.string().nullish(),
      earnings_type: z.enum(['preview', 'recap']).nullish(),
      median_return: z.number().nullish(),
      horizon: z.string().nullish(),
      stars: z.number().nullish(),
      episode_count: z.number().nullish(),
      action_type: z.string().nullish(),
      category: z.enum(['market_wide', 'company']).nullish(),
      sub_category: catalystSubcategoriesSchema.nullish(),
      identifier: z.string().nullish(),
      snakes: z.array(z.string()).nullish(),
      kind: z.union([insightIconDisplayRuleZodEnum, z.literal('')]),
      market_hours: marketHoursEnum.nullish(),
    })
    .catchall(z.unknown()),
  read: z.boolean(),
  relevance: relevanceSchema,
});

const eventSchemaV3 = eventSchema.extend({
  type: z.enum(['kg-insight']),
  metadata: eventMetadataSchemaV3,
});

export const eventFeedResponseSchema = z.object({
  events: z.array(eventSchema),
  next_cursor: z.string().nullish(),
  prev_cursor: z.string().nullish(),
});

export const eventFeedResponseSchemaV3 = z.object({
  events: z.array(eventSchemaV3),
  next_cursor: z.string().nullish(),
  prev_cursor: z.string().nullish(),
});

/**
 * @deprecated
 */
export type EventFeedRequest = z.infer<typeof eventFeedRequestSchema>;
export type EventFeedRequestV2 = z.infer<typeof eventFeedRequestSchemaV2>;
export type EventFeedRequestV3 = z.infer<typeof eventFeedRequestSchemaV3>;
export type EventFeedResponse = z.infer<typeof eventFeedResponseSchema>;
export type EventFeedResponseV3 = z.infer<typeof eventFeedResponseSchemaV3>;
export type EventFeedEvent = z.infer<typeof eventSchema>;
export type EventFeedEventV3 = z.infer<typeof eventSchemaV3>;
export type EventFeedRelevance = z.infer<typeof relevanceSchema>;
export type EventFeedResponseMetadataV3 = z.infer<typeof eventMetadataSchemaV3>;

export const eventFeed = {
  getFilters: {
    path: `${eventFeedBasePath}schemas`,
    schema: eventFeedFiltersSchemas,
  },
  /**
   * @deprecated
   */
  postFeedEvents: {
    path: `${eventFeedBasePath}events`,
    schema: eventFeedResponseSchema,
  },
  postFeedEventsV2: {
    path: `event-feed/v2/events`,
    schema: eventFeedResponseSchema,
  },
  postFeedEventsV3: {
    path: `event-feed/v3/events`,
    schema: eventFeedResponseSchemaV3,
  },
  postEventRead: {
    path: (eventId: string) => `${eventFeedBasePath}events/${eventId}/read`,
  },
};
